import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, UserPlus, CheckCircle2, Camera, Sparkles, Info, AlertTriangle, X, Copy, Users, Search, FileText, Trash2 } from "lucide-react";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  lookupCpfPublic,
  extractOpponentDocumentPublic,
  getPublicChampionshipForInvite,
  listPublicEventCategoriesForInvite,
  listPublicOpponentAthletes,
  checkPublicOpponentCpfExists,
  createPublicOpponentAthlete,
  updatePublicOpponentAthlete,
  deletePublicOpponentAthlete,
} from "@/lib/api/public-opponent.functions";

export const Route = createFileRoute("/inscrever-adversario/$eventId")({
  head: () => ({
    meta: [
      { title: "Cadastrar Atletas do Evento" },
      { name: "description", content: "Cadastre os atletas do seu CT para participar do evento." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PublicOpponentRegister,
});

type Athlete = {
  id: string; name: string; cpf: string | null; birth_date: string | null;
  event_category_id: string | null; category: string | null; avatar_url: string | null;
  above_category?: boolean | null;
  is_goalkeeper?: boolean | null;
  gender?: string | null;
};
type EvCat = {
  id: string; category: string | null; field_name: string | null;
  birth_year_start: number | null; birth_year_end: number | null;
};

function catLabel(c: EvCat) {
  const parts = [c.field_name, c.category].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
}

function catYearsLabel(c: EvCat): string {
  const s = c.birth_year_start, e = c.birth_year_end;
  if (s == null && e == null) return "";
  if (s != null && e != null && s !== e) return `${Math.min(s, e)}-${Math.max(s, e)}`;
  return `${s ?? e}`;
}

function maskCpf(v: string) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

function computeAge(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const d = new Date(birthDate + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a;
}

// Retorna: "ok" | "younger" | "older" | "unknown"
function ageStatus(birthDate: string | null, cat: EvCat | undefined): "ok" | "younger" | "older" | "unknown" {
  if (!birthDate || !cat) return "unknown";
  const y = parseInt(birthDate.slice(0, 4), 10);
  if (!Number.isFinite(y)) return "unknown";
  const s = cat.birth_year_start, e = cat.birth_year_end;
  if (s == null && e == null) return "unknown";
  const lo = Math.min(s ?? y, e ?? y);
  const hi = Math.max(s ?? y, e ?? y);
  if (y >= lo && y <= hi) return "ok";
  if (y > hi) return "younger"; // nasceu depois → mais novo, pode jogar acima
  return "older"; // nasceu antes → mais velho, não pode
}

function PublicOpponentRegister() {
  const { eventId } = Route.useParams();
  const parentMode =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("p") === "1";
  const [event, setEvent] = useState<any>(null);
  const [cats, setCats] = useState<EvCat[]>([]);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<"choose" | "photo" | "manual">("choose");
  const [showCpfInfo, setShowCpfInfo] = useState(false);
  const [confirmManual, setConfirmManual] = useState(false);

  // photo mode - front + back
  const [photoFront, setPhotoFront] = useState<string | null>(null);
  const [photoBack, setPhotoBack] = useState<string | null>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const frontRef = useRef<HTMLInputElement>(null);
  const backRef = useRef<HTMLInputElement>(null);

  // shared form – nome/nascimento SÓ via captura (OCR/CPF), não editáveis
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [birth, setBirth] = useState("");
  const [capturedName, setCapturedName] = useState(false);
  const [capturedBirth, setCapturedBirth] = useState(false);
  const [cpfLoading, setCpfLoading] = useState(false);
  const [suggestedCatId, setSuggestedCatId] = useState<string>("");
  const [portrait, setPortrait] = useState<string | null>(null);
  const [isGoalkeeper, setIsGoalkeeper] = useState(false);
  const [gender, setGender] = useState<string>("M");
  const portraitRef = useRef<HTMLInputElement>(null);

  // multi-categoria: escolha adicional após cadastro
  const [registered, setRegistered] = useState<null | {
    name: string; cpf: string; birth: string; category: string;
    gender: string; goalkeeper: boolean; avatar: string | null;
  }>(null);
  const [extraCatFor, setExtraCatFor] = useState<string | null>(null); // athlete id
  const [extraCatId, setExtraCatId] = useState<string>("");
  const [assignCatFor, setAssignCatFor] = useState<string | null>(null);
  const [assignCatId, setAssignCatId] = useState<string>("");

  const ocr = useServerFn(extractOpponentDocumentPublic);
  const cpfLookup = useServerFn(lookupCpfPublic);
  const fetchChamp = useServerFn(getPublicChampionshipForInvite);
  const fetchCats = useServerFn(listPublicEventCategoriesForInvite);
  const fetchAthletes = useServerFn(listPublicOpponentAthletes);
  const checkCpfExists = useServerFn(checkPublicOpponentCpfExists);
  const createAthlete = useServerFn(createPublicOpponentAthlete);
  const updateAthleteFn = useServerFn(updatePublicOpponentAthlete);
  const deleteAthleteFn = useServerFn(deletePublicOpponentAthlete);

  const pickCategoryFromBirth = (birthDate: string | null): string => {
    if (!birthDate || cats.length === 0) return "";
    const y = parseInt(birthDate.slice(0, 4), 10);
    if (!Number.isFinite(y)) return "";
    const match = cats.find((c) => {
      const s = c.birth_year_start, e = c.birth_year_end;
      if (s == null && e == null) return false;
      const lo = Math.min(s ?? y, e ?? y);
      const hi = Math.max(s ?? y, e ?? y);
      return y >= lo && y <= hi;
    });
    return match?.id ?? "";
  };

  const reload = async () => {
    try {
      const { athletes: rows } = await fetchAthletes({ data: { championship_id: eventId } });
      setAthletes((rows as any) ?? []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { championship } = await fetchChamp({ data: { championship_id: eventId } });
        if (!championship) { setError("Evento não encontrado."); setLoading(false); return; }
        setEvent(championship);
        const { categories } = await fetchCats({ data: { championship_id: eventId } });
        setCats((categories as any as EvCat[]) ?? []);
        if (!parentMode) await reload();
      } catch (e: any) {
        setError(e?.message || "Falha ao carregar evento.");
      } finally {
        setLoading(false);
      }
    })();

    if (parentMode) return;
    // Poll for updates instead of realtime (avoids anon Postgres subscription).
    const iv = window.setInterval(() => { reload(); }, 15000);
    return () => { window.clearInterval(iv); };
  }, [eventId, parentMode]);


  const resetForm = () => {
    setName(""); setCpf(""); setBirth("");
    setCapturedName(false); setCapturedBirth(false);
    setPhotoFront(null); setPhotoBack(null); setSuggestedCatId("");
    setPortrait(null);
    setIsGoalkeeper(false); setGender("M");
    if (frontRef.current) frontRef.current.value = "";
    if (backRef.current) backRef.current.value = "";
    if (portraitRef.current) portraitRef.current.value = "";
    setMode("choose");
  };

  const fileToDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(new Error("Falha ao ler imagem"));
      r.readAsDataURL(file);
    });

  const runOcrWith = async (front: string, back: string | null) => {
    setOcrLoading(true);
    try {
      const ex = await ocr({ data: { dataUrl: front, dataUrlBack: back } });
      if (ex.name) { setName(ex.name); setCapturedName(true); }
      if (ex.cpf) setCpf(ex.cpf);
      if (ex.birth_date) {
        setBirth(ex.birth_date);
        setCapturedBirth(true);
        setSuggestedCatId(pickCategoryFromBirth(ex.birth_date));
      }
      toast.success("Dados extraídos da carteira. Confira e cadastre.");
    } catch (e: any) {
      toast.error(e?.message || "Falha na leitura. Tente novamente.");
    } finally {
      setOcrLoading(false);
    }
  };

  const handlePhoto = async (file: File, side: "front" | "back") => {
    if (file.size > 5 * 1024 * 1024) return toast.error("Máx 5MB");
    try {
      const dataUrl = await fileToDataUrl(file);
      if (side === "front") {
        setPhotoFront(dataUrl);
        void runOcrWith(dataUrl, photoBack);
      } else {
        setPhotoBack(dataUrl);
        if (photoFront) void runOcrWith(photoFront, dataUrl);
      }
    } catch (e: any) {
      toast.error(e?.message || "Falha ao ler a imagem.");
    }
  };

  const runOcr = async () => {
    if (!photoFront) return toast.error("Envie ao menos a foto da frente.");
    await runOcrWith(photoFront, photoBack);
  };

  const cpfAlreadyRegistered = async (cpfDigits: string): Promise<boolean> => {
    try {
      const { exists } = await checkCpfExists({ data: { championship_id: eventId, cpf: cpfDigits } });
      return exists;
    } catch { return false; }
  };

  const handleCpfLookup = async () => {
    const digits = cpf.replace(/\D/g, "");
    if (digits.length !== 11) return;
    setCpfLoading(true);
    try {
      if (await cpfAlreadyRegistered(digits)) {
        toast.error("Esse CPF já foi cadastrado para esse evento.");
        return;
      }
      const r = await cpfLookup({ data: { cpf: digits } });
      if (r.ok) {
        if (r.name) { setName(r.name); setCapturedName(true); }
        if (r.birth_date) {
          setBirth(r.birth_date);
          setCapturedBirth(true);
          setSuggestedCatId(pickCategoryFromBirth(r.birth_date));
        }
        toast.success("Nome e data carregados pelo CPF.");
      } else {
        toast.message(r.error || "Não foi possível consultar o CPF. Use a foto da carteira.");
      }
    } catch (e: any) {
      toast.message(e?.message || "Consulta de CPF indisponível.");
    } finally {
      setCpfLoading(false);
    }
  };

  const currentCat = cats.find((c) => c.id === suggestedCatId);
  const currentStatus = ageStatus(birth || null, currentCat);
  const age = computeAge(birth || null);
  const isAdult = age != null && age >= 18;

  const handleSave = async () => {
    if (!capturedName || !name.trim()) return toast.error("Capture o nome pela foto ou pelo CPF.");
    if (!capturedBirth || !birth) return toast.error("Capture a data de nascimento pela foto ou pelo CPF.");

    const cpfDigits = cpf.replace(/\D/g, "");
    if (cpfDigits.length === 11 && (await cpfAlreadyRegistered(cpfDigits))) {
      return toast.error("Esse CPF já foi cadastrado para esse evento.");
    }

    // Parent mode: categoria opcional. CT mode: continua opcional mas com sugestão.
    let catId: string | null = null;
    let catText: string | null = null;

    if (!parentMode && suggestedCatId) {
      catId = suggestedCatId;
      const sel = cats.find((c) => c.id === suggestedCatId);
      catText = sel?.category || null;
    } else if (isAdult) {
      // Sem categoria selecionada, mas adulto → grava rótulo "Adulto"
      catText = "Adulto";
    }

    setSaving(true);
    try {
      await createAthlete({ data: {
        championship_id: eventId,
        name: name.trim(),
        cpf: cpf || null,
        birth_date: birth || null,
        event_category_id: catId,
        category: catText,
        avatar_url: portrait,
        above_category: !parentMode && currentStatus === "older",
        is_goalkeeper: isGoalkeeper,
        gender: gender || null,
      } });
      setRegistered({
        name: name.trim(),
        cpf: cpf || "—",
        birth: birth || "—",
        category: catText || "—",
        gender: gender === "F" ? "Feminino" : "Masculino",
        goalkeeper: isGoalkeeper,
        avatar: portrait,
      });
      toast.success("Atleta cadastrado!");
      resetForm();
      if (!parentMode) reload();
    } catch (e: any) {
      toast.error(e?.message || "Falha ao cadastrar.");
    } finally {
      setSaving(false);
    }
  };

  const addToAnotherCategory = async (a: Athlete, newCatId: string) => {
    if (!newCatId) return;
    const cat = cats.find((c) => c.id === newCatId);
    const st = ageStatus(a.birth_date, cat);
    if (st === "older") {
      const ok = window.confirm("Este atleta é MAIS VELHO que a categoria. Ele será inscrito ACIMA DA CATEGORIA e destacado em vermelho na súmula. Continuar?");
      if (!ok) return;
    }
    if (athletes.some((x) => x.cpf && a.cpf && x.cpf === a.cpf && x.event_category_id === newCatId)) {
      toast.message("Este atleta já está nesta categoria."); return;
    }
    try {
      await createAthlete({ data: {
        championship_id: eventId,
        name: a.name,
        cpf: a.cpf,
        birth_date: a.birth_date,
        event_category_id: newCatId,
        category: cat?.category || null,
        avatar_url: a.avatar_url,
        above_category: st === "older",
      } });
      toast.success(st === "older" ? `Adicionado em ${catLabel(cat!)} (acima da categoria)` : `Adicionado em ${catLabel(cat!)}`);
      setExtraCatFor(null); setExtraCatId("");
      reload();
    } catch (e: any) { toast.error(e?.message || "Falha ao adicionar."); }
  };

  // Duplica um atleta cadastrado, criando cópia SEM categoria (para depois atribuir).
  const duplicateNoCategory = async (a: Athlete) => {
    try {
      await createAthlete({ data: {
        championship_id: eventId,
        name: a.name, cpf: a.cpf, birth_date: a.birth_date,
        event_category_id: null, category: null, avatar_url: a.avatar_url,
        above_category: false,
      } });
      toast.success("Cópia criada em Sem categoria — atribua a categoria.");
      reload();
    } catch (e: any) { toast.error(e?.message || "Falha ao duplicar."); }
  };

  // Atribui uma categoria a um atleta que está sem categoria (UPDATE, não duplica).
  const assignCategory = async (a: Athlete, newCatId: string) => {
    if (!newCatId) return;
    const cat = cats.find((c) => c.id === newCatId);
    const st = ageStatus(a.birth_date, cat);
    if (st === "older") {
      const ok = window.confirm("Este atleta é MAIS VELHO que a categoria. Ele será marcado como ACIMA DA CATEGORIA (vermelho na súmula). Continuar?");
      if (!ok) return;
    }
    try {
      await updateAthleteFn({ data: {
        id: a.id,
        championship_id: eventId,
        event_category_id: newCatId,
        category: cat?.category || null,
        above_category: st === "older",
      } });
      toast.success(st === "older" ? `Movido para ${catLabel(cat!)} (acima da categoria)` : `Movido para ${catLabel(cat!)}`);
      setAssignCatFor(null); setAssignCatId("");
      reload();
    } catch (e: any) { toast.error(e?.message || "Falha ao atribuir categoria."); }
  };

  // Insere/troca a foto de um atleta já inscrito (clique no avatar da lista).
  const uploadPhotoFor = async (a: Athlete, file: File) => {
    try {
      const dataUrl = await fileToDataUrl(file);
      await updateAthleteFn({ data: { id: a.id, championship_id: eventId, avatar_url: dataUrl } });
      toast.success(`Foto de ${a.name} atualizada.`);
      reload();
    } catch (e: any) { toast.error(e?.message || "Falha ao enviar a foto."); }
  };


  const removeEntry = async (id: string) => {
    try {
      await deleteAthleteFn({ data: { id, championship_id: eventId } });
      toast.success("Removido"); reload();
    } catch (e: any) { toast.error(e?.message || "Falha ao remover."); }
  };


  const exportAthletesPdf = () => {
    if (athletes.length === 0) { toast.message("Nenhum atleta cadastrado ainda."); return; }
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const marginX = 14;
      let y = 16;
      const evName = event?.name || "Evento";
      doc.setFont("helvetica", "bold"); doc.setFontSize(14);
      doc.text("Atletas Inscritos", marginX, y); y += 6;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      doc.text(evName, marginX, y); y += 5;
      doc.text(`Total: ${athletes.length} atleta(s) · ${new Date().toLocaleString("pt-BR")}`, marginX, y); y += 6;
      doc.setDrawColor(200); doc.line(marginX, y, 200 - marginX, y); y += 4;

      grouped.forEach((g) => {
        if (y > 275) { doc.addPage(); y = 16; }
        doc.setFont("helvetica", "bold"); doc.setFontSize(11);
        const catTitle = g.cat ? catLabel(g.cat) + (catYearsLabel(g.cat) ? ` (${catYearsLabel(g.cat)})` : "") : "Sem categoria";
        doc.text(`${catTitle} — ${g.items.length}`, marginX, y); y += 5;
        doc.setFont("helvetica", "normal"); doc.setFontSize(9);
        g.items.forEach((a, idx) => {
          if (y > 285) { doc.addPage(); y = 16; }
          const st = ageStatus(a.birth_date, g.cat ?? undefined);
          const above = st === "older";
          const bd = a.birth_date ? new Date(a.birth_date + "T00:00:00").toLocaleDateString("pt-BR") : "—";
          const line = `${idx + 1}. ${a.name}  ·  CPF ${a.cpf ?? "—"}  ·  Nasc. ${bd}`;
          if (above) { doc.setTextColor(200, 0, 0); doc.setFont("helvetica", "bold"); }
          else { doc.setTextColor(0); doc.setFont("helvetica", "normal"); }
          doc.text(line, marginX + 2, y);
          if (above) { doc.text("  ⚠ ACIMA DA CATEGORIA", marginX + 2, y + 3.5); y += 3.5; }
          y += 5;
        });
        doc.setTextColor(0);
        y += 2;
      });

      const fileName = `atletas-${(evName || "evento").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
      doc.save(fileName);
      toast.success("PDF gerado");
    } catch (e: any) {
      toast.error(e?.message || "Falha ao gerar PDF");
    }
  };

  const buildWhatsappSummary = (): string => {
    const evName = event?.name || "Evento";
    const lines: string[] = [];
    lines.push(`*Atletas Inscritos — ${evName}*`);
    lines.push(`Total: ${athletes.length}`);
    lines.push("");
    grouped.forEach((g) => {
      const catTitle = g.cat
        ? catLabel(g.cat) + (catYearsLabel(g.cat) ? ` (${catYearsLabel(g.cat)})` : "")
        : "Sem categoria";
      lines.push(`*${catTitle}* — ${g.items.length}`);
      g.items.forEach((a, idx) => {
        const year = a.birth_date ? a.birth_date.slice(0, 4) : "—";
        const cat = a.category || (g.cat?.category ?? "");
        const catTxt = cat ? ` · ${cat}` : "";
        lines.push(`${idx + 1}. ${a.name} (${year})${catTxt}`);
      });
      lines.push("");
    });
    return lines.join("\n").trim();
  };

  const copyWhatsappSummary = async () => {
    if (athletes.length === 0) { toast.message("Nenhum atleta cadastrado ainda."); return; }
    try {
      await navigator.clipboard.writeText(buildWhatsappSummary());
      toast.success("Lista copiada — cole no WhatsApp");
    } catch {
      toast.error("Não foi possível copiar. Selecione manualmente.");
    }
  };

  // Agrupa por categoria (para CT)
  const grouped = useMemo(() => {
    const map = new Map<string, { cat: EvCat | null; items: Athlete[] }>();
    for (const a of athletes) {
      const key = a.event_category_id ?? "__none__";
      if (!map.has(key)) {
        map.set(key, { cat: cats.find((c) => c.id === a.event_category_id) ?? null, items: [] });
      }
      map.get(key)!.items.push(a);
    }
    // ordem: categorias na ordem de `cats`, depois "sem categoria"
    const ordered: Array<{ key: string; cat: EvCat | null; items: Athlete[] }> = [];
    for (const c of cats) {
      const g = map.get(c.id);
      if (g) ordered.push({ key: c.id, ...g });
    }
    const none = map.get("__none__");
    if (none) ordered.push({ key: "__none__", ...none });
    return ordered;
  }, [athletes, cats]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="max-w-md w-full"><CardContent className="pt-6 text-center">{error}</CardContent></Card>
    </div>
  );

  const nameField = (
    <div>
      <Label className="text-xs">Nome completo * <span className="text-muted-foreground">(preenchido pela captura)</span></Label>
      <Input value={name} readOnly placeholder="Será preenchido automaticamente" className="bg-muted cursor-not-allowed" />
    </div>
  );
  const birthField = (
    <div>
      <Label className="text-xs">Data de nascimento * <span className="text-muted-foreground">(preenchida pela captura)</span></Label>
      <Input type="date" value={birth} readOnly className="bg-muted cursor-not-allowed" />
      {isAdult && <div className="text-[11px] text-muted-foreground mt-1">Idade {age} anos — categoria <strong>Adulto</strong>.</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{event?.name}</CardTitle>
            <div className="mt-2 grid gap-1 text-sm">
              <div className="flex items-center gap-2">
                <span aria-hidden>🗓️</span>
                <span className="font-medium">
                  {event?.start_date
                    ? new Date(event.start_date + "T00:00:00").toLocaleDateString("pt-BR")
                    : "Data a definir"}
                </span>
              </div>
              {event?.location_name && (
                <div className="flex items-center gap-2">
                  <span aria-hidden>📍</span>
                  <span>{event.location_name}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-muted-foreground">
                <span aria-hidden>⏳</span>
                <span>
                  Link válido até{" "}
                  <strong>
                    {event?.end_date || event?.start_date
                      ? new Date((event.end_date || event.start_date) + "T00:00:00").toLocaleDateString("pt-BR")
                      : "a data do evento"}
                  </strong>{" "}
                  (data do evento).
                </span>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <p className="text-sm">
              {parentMode
                ? "Cadastre seu atleta abaixo. Os dados vêm da foto da carteira de identidade ou do CPF."
                : "Cadastre os atletas do seu Centro de Treinamento. Um mesmo atleta pode participar de mais de uma categoria (a dele ou inferior)."}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus className="h-5 w-5" /> Novo Atleta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {mode === "choose" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  className="h-24 flex-col gap-1 bg-emerald-500 hover:bg-emerald-600 text-white border border-emerald-500"
                  onClick={() => setMode("photo")}
                >
                  <Camera className="h-6 w-6" />
                  <span>Foto da Carteira (frente e verso)</span>
                  <span className="text-[10px] opacity-90">Preenche automaticamente · dispensa carteira no jogo</span>
                </Button>
                <Button
                  className="h-24 flex-col gap-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground border border-destructive"
                  onClick={() => setConfirmManual(true)}
                >
                  <UserPlus className="h-6 w-6" />
                  <span>Preencher manualmente</span>
                  <span className="text-[10px] opacity-90">Exige carteira no dia do jogo</span>
                </Button>
              </div>
            )}

            {mode === "photo" && (
              <>
                <Alert>
                  <Sparkles className="h-4 w-4" />
                  <AlertTitle>Foto da Carteira de Identidade</AlertTitle>
                  <AlertDescription>
                    Envie a <strong>frente</strong> e o <strong>verso</strong> da carteira. Ao enviar aqui,
                    <strong> não será necessário apresentá-la no momento do jogo</strong>.
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(["front", "back"] as const).map((side) => {
                    const preview = side === "front" ? photoFront : photoBack;
                    const ref = side === "front" ? frontRef : backRef;
                    const setPreview = side === "front" ? setPhotoFront : setPhotoBack;
                    const label = side === "front" ? "Frente" : "Verso (opcional)";
                    return (
                      <div key={side} className="rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 p-3 space-y-2">
                        <div className="text-xs font-semibold flex items-center justify-between">
                          <span>{label}</span>
                          {preview && (
                            <button type="button" className="text-muted-foreground hover:text-destructive"
                              onClick={() => { setPreview(null); if (ref.current) ref.current.value = ""; }}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        {preview ? (
                          <img src={preview} alt={label} className="w-full h-32 object-cover rounded border" />
                        ) : (
                          <label className="flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed cursor-pointer hover:bg-primary/10">
                            <Camera className="h-6 w-6 text-primary" />
                            <span className="text-[11px] mt-1">Toque para tirar/enviar</span>
                            <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden"
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhoto(f, side); }} />
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>

                <Button type="button" className="w-full" variant="secondary"
                  disabled={!photoFront || ocrLoading} onClick={runOcr}>
                  {ocrLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Ler documento novamente
                </Button>

                {nameField}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">CPF <span className="text-muted-foreground">(via captura)</span></Label>
                    <Input value={cpf} readOnly placeholder="Preenchido pela foto" className="bg-muted cursor-not-allowed" />
                  </div>
                  {birthField}
                </div>
              </>
            )}

            {mode === "manual" && (
              <>
                {showCpfInfo && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertTitle>Dica</AlertTitle>
                    <AlertDescription>
                      Se você enviar a <strong>foto da carteira</strong> (frente e verso), não precisará
                      apresentá-la no dia do jogo.{" "}
                      <Button variant="link" className="h-auto p-0 align-baseline"
                        onClick={() => { setShowCpfInfo(false); setMode("photo"); }}>
                        Tirar fotos
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                <div>
                  <Label className="text-xs">CPF *</Label>
                  <div className="flex gap-2">
                    <Input
                      value={cpf}
                      onFocus={() => setShowCpfInfo(true)}
                      onChange={(e) => {
                        const v = maskCpf(e.target.value);
                        setCpf(v);
                        if (capturedBirth || capturedName) {
                          setBirth(""); setName("");
                          setCapturedBirth(false); setCapturedName(false);
                          setSuggestedCatId("");
                        }
                      }}
                      onBlur={handleCpfLookup}
                      placeholder="000.000.000-00"
                      inputMode="numeric"
                    />
                    <Button type="button" onClick={handleCpfLookup}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      disabled={cpfLoading || cpf.replace(/\D/g, "").length !== 11}>
                      {cpfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" />Buscar CPF</>}
                    </Button>
                  </div>
                </div>
                {nameField}
                {birthField}
              </>
            )}

            {/* Foto do atleta (portrait) — opcional, aparece nos dois modos */}
            {mode !== "choose" && (
              <div className="rounded-lg border bg-muted/20 p-3">
                <Label className="text-xs font-semibold">Foto do atleta <span className="text-muted-foreground">(opcional)</span></Label>
                <p className="text-[11px] text-muted-foreground mb-2">Aparecerá ao lado do nome na súmula do jogo.</p>
                {portrait ? (
                  <div className="relative w-24">
                    <img src={portrait} alt="Foto do atleta" className="w-24 h-24 object-cover rounded-full border shadow-sm" />
                    <button type="button" className="absolute -top-1 -right-1 rounded-full bg-white border shadow p-0.5 hover:text-destructive"
                      onClick={() => { setPortrait(null); if (portraitRef.current) portraitRef.current.value = ""; }}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 rounded-md border-2 border-dashed border-green-500 bg-green-50 text-green-800 px-3 py-2 cursor-pointer hover:bg-green-100 text-xs font-medium">
                    <Camera className="h-4 w-4 text-green-700" /> Enviar foto do rosto
                    <input ref={portraitRef} type="file" accept="image/*" capture="user" className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 4 * 1024 * 1024) { toast.error("Máx 4MB"); return; }
                        try { setPortrait(await fileToDataUrl(f)); } catch (err: any) { toast.error(err?.message || "Falha ao ler foto"); }
                      }} />
                  </label>
                )}
              </div>
            )}

            {/* Goleiro + Sexo */}
            {mode !== "choose" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 rounded-md border p-3">
                  <input id="isGk" type="checkbox" className="h-4 w-4" checked={isGoalkeeper}
                    onChange={(e) => setIsGoalkeeper(e.target.checked)} />
                  <Label htmlFor="isGk" className="text-sm cursor-pointer">🧤 É goleiro</Label>
                </div>
                <div>
                  <Label className="text-xs">Sexo</Label>
                  <Select value={gender} onValueChange={setGender}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">♂️ Masculino</SelectItem>
                      <SelectItem value="F">♀️ Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Categoria: só CT vê. Parent salva sem categoria (ou como Adulto). */}
            {mode !== "choose" && !parentMode && cats.length > 0 && (
              <div>
                <Label className="text-xs">Categoria (Sub)</Label>
                <Select value={suggestedCatId} onValueChange={setSuggestedCatId}>
                  <SelectTrigger className={currentStatus === "older" ? "border-destructive text-destructive" : ""}>
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => {
                      const st = ageStatus(birth || null, c);
                      return (
                        <SelectItem key={c.id} value={c.id}>
                          <span className={st === "older" ? "text-destructive" : ""}>
                            {catLabel(c)}{catYearsLabel(c) ? ` (${catYearsLabel(c)})` : ""}
                            {st === "older" ? " — mais velho" : st === "younger" ? " — joga acima" : ""}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {birth && (
                  <div className={
                    "mt-2 rounded-md border p-2 text-xs " +
                    (currentStatus === "older"
                      ? "border-destructive bg-destructive/10 text-destructive font-medium"
                      : "border-muted bg-muted/40 text-muted-foreground")
                  }>
                    {currentCat ? (
                      <>
                        Sugerida: <strong>{catLabel(currentCat)}</strong>{catYearsLabel(currentCat) ? ` (${catYearsLabel(currentCat)})` : ""}
                        {currentStatus === "younger" && " — atleta é mais novo, pode jogar nesta categoria."}
                        {currentStatus === "older" && " — ⚠ ACIMA DA CATEGORIA. Permitido, mas o atleta ficará destacado em vermelho na súmula."}
                      </>
                    ) : isAdult ? (
                      <>Atleta adulto ({age} anos). Selecione uma categoria compatível ou deixe sem categoria.</>
                    ) : (
                      <>Nenhuma categoria compatível com {birth.slice(0, 4)}.</>
                    )}
                  </div>
                )}
              </div>
            )}

            {mode !== "choose" && (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={resetForm} disabled={saving}>Voltar</Button>
                <Button className="flex-1" onClick={handleSave}
                  disabled={saving || !capturedName || !capturedBirth}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Cadastrar Atleta
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {parentMode ? (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">
              Após cadastrar seu atleta, você pode fechar esta página. A confirmação foi registrada para o Centro de Treinamento organizador.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" /> Atletas por categoria <Badge variant="secondary">{athletes.length}</Badge>
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Um mesmo atleta pode participar de mais de uma categoria (a dele ou inferior).
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={exportAthletesPdf} disabled={athletes.length === 0}
                    className="shrink-0 border-green-600 text-green-700 hover:bg-green-50">
                    <FileText className="h-4 w-4 mr-1" /> Exportar PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={copyWhatsappSummary} disabled={athletes.length === 0}
                    className="shrink-0 border-blue-600 text-blue-700 hover:bg-blue-50">
                    <Copy className="h-4 w-4 mr-1" /> Copiar p/ WhatsApp
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {grouped.length === 0 && (
                <p className="text-sm text-muted-foreground">Nenhum atleta cadastrado ainda.</p>
              )}
              {grouped.map((g) => (
                <div key={g.key} className="border-2 border-blue-500 rounded-md">
                  <div className="px-3 py-2 bg-blue-50 border-b-2 border-blue-500 rounded-t-md flex items-center justify-between">
                    <div className="text-sm font-semibold text-blue-900">
                      {g.cat ? catLabel(g.cat) : "Sem categoria"}
                      {g.cat && catYearsLabel(g.cat) ? <span className="text-xs text-blue-700 ml-1">({catYearsLabel(g.cat)})</span> : null}
                    </div>
                    <Badge variant="outline" className="border-blue-500 text-blue-700">{g.items.length}</Badge>
                  </div>
                  <div className="divide-y divide-blue-200">
                    {g.items.map((a) => {
                      const st = ageStatus(a.birth_date, g.cat ?? undefined);
                      const isOlder = st === "older";
                      const noCat = !a.event_category_id;
                      return (
                        <div key={a.id} className={"p-2 text-sm " + (isOlder ? "bg-destructive/5" : "")}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 min-w-0">
                              <label
                                title="Clique para inserir/trocar a foto do atleta"
                                className="relative shrink-0 cursor-pointer group"
                              >
                                <input
                                  type="file" accept="image/*" capture="environment" className="hidden"
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.currentTarget.value = "";
                                    if (f) void uploadPhotoFor(a, f);
                                  }}
                                />
                                {a.avatar_url ? (
                                  <img src={a.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover border" />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-muted border shadow-inner flex items-center justify-center">
                                    <Camera className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                )}
                                <span className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                                  <Camera className="h-4 w-4 text-white" />
                                </span>
                              </label>

                              <div className="min-w-0">
                                <div className={"font-medium flex items-center gap-1 " + (isOlder ? "text-destructive" : "")}>
                                  {a.is_goalkeeper && <span title="Goleiro">🧤</span>}
                                  {a.gender === "F" ? <span title="Feminino">♀️</span> : a.gender === "M" ? <span title="Masculino">♂️</span> : null}
                                  {a.name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {a.cpf ?? "—"}
                                  {a.birth_date ? ` · ${new Date(a.birth_date + "T00:00:00").toLocaleDateString("pt-BR")} (${a.birth_date.slice(0, 4)})` : ""}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              {isOlder && <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Acima da categoria</Badge>}
                              {st === "younger" && <Badge variant="secondary">Joga acima</Badge>}
                              {st === "ok" && <Badge>OK</Badge>}
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                title="Remover" onClick={() => removeEntry(a.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {noCat && assignCatFor === a.id ? (
                            <div className="mt-2 flex items-center gap-2">
                              <Select value={assignCatId} onValueChange={setAssignCatId}>
                                <SelectTrigger className="h-8"><SelectValue placeholder="Escolha a categoria" /></SelectTrigger>
                                <SelectContent>
                                  {cats.map((c) => {
                                    const s2 = ageStatus(a.birth_date, c);
                                    return (
                                      <SelectItem key={c.id} value={c.id} >
                                        <span className={s2 === "older" ? "text-destructive" : ""}>
                                          {catLabel(c)}{catYearsLabel(c) ? ` (${catYearsLabel(c)})` : ""}
                                          {s2 === "older" ? " — acima da categoria" : s2 === "younger" ? " — joga acima" : ""}
                                        </span>
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              <Button size="sm" onClick={() => assignCategory(a, assignCatId)} disabled={!assignCatId}>
                                Atribuir
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setAssignCatFor(null); setAssignCatId(""); }}>
                                Cancelar
                              </Button>
                            </div>
                          ) : extraCatFor === a.id ? (
                            <div className="mt-2 flex items-center gap-2">
                              <Select value={extraCatId} onValueChange={setExtraCatId}>
                                <SelectTrigger className="h-8"><SelectValue placeholder="Escolha outra categoria" /></SelectTrigger>
                                <SelectContent>
                                  {cats
                                    .filter((c) => c.id !== a.event_category_id)
                                    .map((c) => {
                                      const s2 = ageStatus(a.birth_date, c);
                                      return (
                                        <SelectItem key={c.id} value={c.id} >
                                          <span className={s2 === "older" ? "text-destructive" : ""}>
                                            {catLabel(c)}{catYearsLabel(c) ? ` (${catYearsLabel(c)})` : ""}
                                            {s2 === "older" ? " — acima da categoria" : s2 === "younger" ? " — joga acima" : ""}
                                          </span>
                                        </SelectItem>
                                      );
                                    })}
                                </SelectContent>
                              </Select>
                              <Button size="sm" onClick={() => addToAnotherCategory(a, extraCatId)} disabled={!extraCatId}>
                                Adicionar
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setExtraCatFor(null); setExtraCatId(""); }}>
                                Cancelar
                              </Button>
                            </div>
                          ) : (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {noCat && cats.length > 0 && (
                                <Button size="sm" className="h-7 text-xs bg-emerald-500 hover:bg-emerald-600 text-white"
                                  onClick={() => { setAssignCatFor(a.id); setAssignCatId(""); }}>
                                  Atribuir categoria
                                </Button>
                              )}
                              {!noCat && (
                                <Button size="sm" variant="outline" className="h-7 text-xs"
                                  onClick={() => { setExtraCatFor(a.id); setExtraCatId(""); }}>
                                  <Copy className="h-3 w-3 mr-1" /> Duplicar em outra categoria
                                </Button>
                              )}
                              {!noCat && (
                                <Button size="sm" variant="ghost" className="h-7 text-xs"
                                  onClick={() => duplicateNoCategory(a)}>
                                  <Copy className="h-3 w-3 mr-1" /> Duplicar sem categoria
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      <AlertDialog open={confirmManual} onOpenChange={setConfirmManual}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Preencher sem a foto da carteira?</AlertDialogTitle>
            <AlertDialogDescription>
              Enviando a <strong>foto da carteira de identidade</strong> (frente e verso), o atleta
              <strong> não precisa apresentá-la no dia do jogo</strong>.
              <br /><br />
              No preenchimento <strong>manual</strong>, a apresentação da carteira será
              <strong> obrigatória no momento do jogo</strong>. Deseja continuar assim mesmo?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmManual(false); setMode("manual"); }}>
              Continuar sem foto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!registered} onOpenChange={(o) => !o && setRegistered(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>✅ Cadastro realizado com sucesso!</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2 text-left">
                {registered?.avatar && (
                  <img
                    src={registered.avatar}
                    alt={`Foto de ${registered.name}`}
                    className="h-24 w-24 rounded-full object-cover border mx-auto"
                  />
                )}
                <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
                  <div><b>Nome:</b> {registered?.name}</div>
                  <div><b>CPF:</b> {registered?.cpf}</div>
                  <div><b>Nascimento:</b> {registered?.birth}</div>
                  <div><b>Categoria:</b> {registered?.category}</div>
                  <div><b>Sexo:</b> {registered?.gender}</div>
                  <div><b>Goleiro:</b> {registered?.goalkeeper ? "Sim" : "Não"}</div>
                </div>
                <p className="text-xs">Confira os dados acima. Em caso de erro, procure a organização do evento.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setRegistered(null)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
